import { useMutation } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import { Alert } from "components/base/Toast";
import { useState } from "react";
import GenerateCpcCertificateFields from "./GenerateCpcCertificateFields";
import { GENERATE_CPC_PDF } from "./graphql";

const DEFAULT_CERTIFY_TEXT =
  "I (we) hereby certify that the component / product contained within this shipment complies with the following applicable rules, bans, regulations and standards enforced by the CPSC based on testing.";

const DEFAULT_NOTES = `CPSIA – Consumer Product Safety Improvement Act
FHSA – Federal Hazardous Substances Act
CPSA – Consumer Product Safety Act
FFA – Flammable Fabrics Act
RSA – Refrigerator Safety Act
P.L. – Public Law
CGBPA – Children's Gasoline Burn Prevention Act
DSA – Drywall Safety Act
PPPA – Poison Prevention Packaging Act`;

const DEFAULT_TABLE_ROWS = [
  {
    standard:
      "ASTM F963-23, Standard Consumer Safety Specification for Toy Safety",
    lawAct: "CPSIA",
    federalNo: "16 CFR Part 1250",
  },
  {
    standard:
      "CPSIA Section 101(a)(2), Total Lead Content in Substrate Materials",
    lawAct: "CPSIA",
    federalNo: "CPSIA Section 101(a)(2)",
  },
  {
    standard: "CPSIA Section 108, Phthalates Content",
    lawAct: "CPSIA",
    federalNo: "CPSIA Section 108",
  },
  {
    standard: "16 CFR Part 1500, Federal Hazardous Substances Act (FHSA)",
    lawAct: "FHSA",
    federalNo: "16 CFR Part 1500",
  },
  {
    standard: "16 CFR 1500.44, Flammable Solids",
    lawAct: "FHSA",
    federalNo: "16 CFR 1500.44",
  },
  {
    standard: "CPSIA Section 103, Tracking Labels for Children’s Products",
    lawAct: "CPSIA",
    federalNo: "CPSIA Section 103",
  },
];

const defaultText = (value, fallback = "") =>
  String(value === null || value === undefined ? fallback : value).trim();

const GenerateCpcPdfView = ({ type = "CPC", defaultValues = {}, onCompleted }) => {
  const imageUrls = Array.isArray(defaultValues.imageUrls)
    ? defaultValues.imageUrls.filter(Boolean)
    : [];
  const [usImporterDomesticManufacturer, setUsImporterDomesticManufacturer] =
    useState(`Pete Widmann
WABOBA INC
3850 Holcomb Bridge Road
Suite 270
Peachtree Corners GA 30092
United States
E-MAIL: pete@waboba.com
Tel No.: 678-232-0967`);
  const [personMaintainingRecords, setPersonMaintainingRecords] =
    useState(`Stella Huang
Waboba (Guangzhou) Sporting Goods Co., Limited
Room1501, Xinbaoli Building, No.2nd Zhong Shan
Liu Road, YueXiu District, Guangzhou City, China
510180
E-MAIL: stella@waboba.com
TEL. NO.: +86(20) 88902847`);
  const [form, setForm] = useState({
    certificateNumber: defaultValues.certificateNumber || "",
    sampleDescription: defaultText(defaultValues.sampleDescription),
    manufacturer: defaultText(defaultValues.manufacturer, "Waboba AB"),
    styleNos: defaultText(defaultValues.styleNos),
    placeOfManufacturer: defaultText(defaultValues.placeOfManufacturer, "CHINA"),
    countryOfOrigin: defaultText(defaultValues.countryOfOrigin, "CHINA"),
    dateOfManufacturer: defaultText(defaultValues.dateOfManufacturer),
    placeOfTesting: defaultText(defaultValues.placeOfTesting),
    testReportNumber: defaultText(defaultValues.testReportNumber),
    dateOfTesting: defaultText(defaultValues.dateOfTesting),
    certifyText: defaultText(defaultValues.certifyText, DEFAULT_CERTIFY_TEXT),
    notes: defaultText(defaultValues.notes, DEFAULT_NOTES),
  });
  const [rows, setRows] = useState(DEFAULT_TABLE_ROWS);
  const [generateCpcPdf, generateCpcPdfRes] = useMutation(GENERATE_CPC_PDF, {
    onError(error) {
      Alert("error", parseError(error));
    },
  });

  function onChange(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function addRowHandler() {
    setRows((prev) => [...prev, { standard: "", lawAct: "", federalNo: "" }]);
  }

  function changeRowHandler(index, key, value) {
    setRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [key]: value } : row,
      ),
    );
  }

  function removeRowHandler(index) {
    setRows((prev) => prev.filter((_, rowIndex) => rowIndex !== index));
  }

  function generatePdfHandler() {
    const standards = rows
      .map((row) => ({
        standard: defaultText(row.standard),
        lawAct: defaultText(row.lawAct),
        federalNo: defaultText(row.federalNo),
      }))
      .filter((row) => row.standard || row.lawAct || row.federalNo);

    if (standards.length === 0) {
      Alert("error", "Please add at least one standard row.");
      return;
    }

    if (standards.some((row) => !row.standard || !row.lawAct || !row.federalNo)) {
      Alert("error", "Please complete all fields for each standard row.");
      return;
    }

    const certificateCode = defaultText(form.certificateNumber) || `${type}-${Date.now()}`;
    const filename = `${certificateCode}.pdf`;
    const input = {
      certificateType: defaultText(type, "CPC"),
      certificateCode,
      usImporterDomesticManufacturer: defaultText(usImporterDomesticManufacturer),
      personMaintainingRecords: defaultText(personMaintainingRecords),
      sampleDescription: defaultText(form.sampleDescription),
      manufacturer: defaultText(form.manufacturer),
      styleNos: defaultText(form.styleNos),
      placeOfManufacturer: defaultText(form.placeOfManufacturer),
      countryOfOrigin: defaultText(form.countryOfOrigin),
      dateOfManufacturer: defaultText(form.dateOfManufacturer),
      placeOfTesting: defaultText(form.placeOfTesting),
      testReportNumber: defaultText(form.testReportNumber),
      dateOfTesting: defaultText(form.dateOfTesting),
      certifyText: defaultText(form.certifyText),
      notes: defaultText(form.notes),
      imageUrls,
      standards,
    };

    generateCpcPdf({ variables: { input, filename } }).then((res) => {
      const fileUrl = res?.data?.generateCpcPdf?.fileUrl;
      if (!fileUrl) {
        Alert("error", "Failed to generate PDF.");
        return;
      }
      Alert("success", "PDF generated.");
      if (onCompleted) {
        onCompleted(fileUrl);
      }
    });
  }

  return (
    <div className="p-6 md:p-10 space-y-6 text-sm">
      <div className="card space-y-10">
        <GenerateCpcCertificateFields
          form={form}
          onChange={onChange}
          imageUrls={imageUrls}
          rows={rows}
          addRowHandler={addRowHandler}
          changeRowHandler={changeRowHandler}
          removeRowHandler={removeRowHandler}
          usImporterDomesticManufacturer={usImporterDomesticManufacturer}
          setUsImporterDomesticManufacturer={setUsImporterDomesticManufacturer}
          personMaintainingRecords={personMaintainingRecords}
          setPersonMaintainingRecords={setPersonMaintainingRecords}
        />
      </div>

      <div className="flex justify-end">
        <Button primary loading={generateCpcPdfRes.loading} onClick={generatePdfHandler}>
          Generate PDF
        </Button>
      </div>
    </div>
  );
};

export default GenerateCpcPdfView;
